/**
 * The export operation's state machine, shared by the table export (PV18,
 * `useWatchlistExport`) and the pitch export (PV19,
 * `useWatchlistPitchExport`): one run at a time, `collecting` → `rendering`
 * → `done` | `failed`, progress from the pages read, `retry()` re-runs the
 * last format from scratch, `cancel()` retires the run between pages so its
 * pending page resolves into nothing.
 *
 * The platform capability fork lives here, not in a View: a file hand-off
 * needs a browser, so on native `available` is false and `start()` runs
 * nothing — the control says "Available on web" instead of a silent no-op.
 * @module features/scouting/hooks/useExportOperation
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";

import type { WatchlistExportFormat } from "@/features/scouting/utils/watchlistExport/exportFilename";

export type { WatchlistExportFormat };

/** Where the operation is. */
export type WatchlistExportStatus =
  | "idle"
  | "collecting"
  | "rendering"
  | "done"
  | "failed";

/** Players collected so far against the list's total. */
export interface WatchlistExportProgress {
  readonly loaded: number;
  readonly total: number;
}

/** What the Export control renders from. */
export interface WatchlistExportController {
  /** False on native: every format reads "Available on web". */
  readonly available: boolean;
  readonly format: WatchlistExportFormat | null;
  readonly status: WatchlistExportStatus;
  readonly progress: WatchlistExportProgress;
  /** The failure message, verbatim, while `status` is `failed`. */
  readonly error: string | null;
  /** The written filename while `status` is `done`. */
  readonly filename: string | null;
  readonly start: (format: WatchlistExportFormat) => void;
  /** Restarts the last format from page 1. */
  readonly retry: () => void;
  /** Stops between pages; no file is written. */
  readonly cancel: () => void;
}

/** What one run hands its executor. */
export interface ExportRunContext {
  /** False once the run was retired by a cancel, a new start or unmount. */
  readonly isCurrent: () => boolean;
  /** Progress after each page. */
  readonly onProgress: (loaded: number, total: number) => void;
  /** Every page is read; the file is being written. */
  readonly onRendering: () => void;
}

/** Inputs for {@link useExportOperation}. */
export interface UseExportOperationOptions {
  /** The export exists (a real list is selected). */
  readonly enabled: boolean;
  /**
   * What the export is FOR — the selected list plus the read inputs. When it
   * changes, the active run is retired and the controller reads idle again,
   * so a run started for list A never reports its filename, progress or
   * failure on the control now rendered for list B (CodeRabbit on #7399).
   * The consumer keeps its hook mounted across selection changes, so this
   * is the reset the key-remount pattern would otherwise provide.
   */
  readonly scope: string;
  /**
   * Reads, renders and writes one file; resolves with the filename written.
   * Throw {@link EXPORT_CANCELLED} (or let `isCurrent()` guard the pages) to
   * end a retired run silently.
   */
  readonly execute: (
    format: WatchlistExportFormat,
    context: ExportRunContext
  ) => Promise<string>;
}

/** The state the controller exposes. */
interface ExportState {
  readonly format: WatchlistExportFormat | null;
  readonly status: WatchlistExportStatus;
  readonly progress: WatchlistExportProgress;
  readonly error: string | null;
  readonly filename: string | null;
}

const IDLE: ExportState = {
  format: null,
  status: "idle",
  progress: { loaded: 0, total: 0 },
  error: null,
  filename: null,
};

/** Thrown when a run is cancelled between pages; never surfaces. */
export const EXPORT_CANCELLED = new Error("Export cancelled");

/**
 * The message an export failure shows, verbatim.
 * @param error - Whatever was thrown
 * @returns The message.
 */
const messageOf = (error: unknown): string =>
  error instanceof Error && error.message ? error.message : String(error);

/**
 * Owns one export at a time. See the module doc for the contract.
 * @param options - Enablement and the executor
 * @param options.enabled - The export exists
 * @param options.execute - Reads, renders and writes; resolves the filename
 * @param options.scope - What the export is for; a change retires the run
 * @returns The controller the Export control renders from.
 */
export const useExportOperation = ({
  enabled,
  execute,
  scope,
}: UseExportOperationOptions): WatchlistExportController => {
  const available = Platform.OS === "web";
  // The state carries the scope it was written under; a state from another
  // scope reads as idle (derived, never reset in an effect).
  const [scoped, setScoped] = useState<{
    readonly scope: string;
    readonly state: ExportState;
  }>({ scope, state: IDLE });
  const state = scoped.scope === scope && enabled ? scoped.state : IDLE;
  const setState = useCallback(
    (next: ExportState | ((previous: ExportState) => ExportState)) =>
      setScoped(previous => ({
        scope,
        state:
          typeof next === "function"
            ? next(previous.scope === scope ? previous.state : IDLE)
            : next,
      })),
    [scope]
  );
  // The run id: a start or a cancel retires every earlier run.
  const runRef = useRef(0);
  const lastFormatRef = useRef<WatchlistExportFormat | null>(null);
  const mountedRef = useRef(true);

  const runExport = useCallback(
    async (format: WatchlistExportFormat) => {
      if (!available || !enabled) return;
      const run = runRef.current + 1;
      const update = (next: Partial<ExportState>) => {
        if (mountedRef.current && runRef.current === run) {
          setState(previous => ({ ...previous, ...next }));
        }
      };
      runRef.current = run;
      lastFormatRef.current = format;
      update({
        format,
        status: "collecting",
        progress: { loaded: 0, total: 0 },
        error: null,
        filename: null,
      });
      try {
        const filename = await execute(format, {
          isCurrent: () => runRef.current === run,
          onProgress: (loaded, total) =>
            update({ progress: { loaded, total } }),
          onRendering: () => update({ status: "rendering" }),
        });
        update({ status: "done", filename });
      } catch (error: unknown) {
        if (error === EXPORT_CANCELLED) return;
        update({ status: "failed", error: messageOf(error) });
      }
    },
    [available, enabled, execute, setState]
  );

  const start = useCallback(
    (format: WatchlistExportFormat) => {
      void runExport(format);
    },
    [runExport]
  );

  const retry = useCallback(() => {
    if (lastFormatRef.current) void runExport(lastFormatRef.current);
  }, [runExport]);

  const cancel = useCallback(() => {
    runRef.current += 1;
    setState(IDLE);
  }, [setState]);

  const controller = useMemo(
    () => ({ available, ...state, start, retry, cancel }),
    [available, state, start, retry, cancel]
  );

  // Strict Mode runs the cleanup and then the setup again on the same
  // instance, so the setup must restore the flag (CodeRabbit on #7399).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      runRef.current += 1;
    };
  }, []);

  // A scope or enablement change retires the active run and forgets the
  // last format, so Retry cannot replay list A's export on list B. Refs
  // only — the visible state is derived from `scope` above.
  useEffect(() => {
    runRef.current += 1;
    lastFormatRef.current = null;
  }, [scope, enabled]);

  return controller;
};
